/**
 * Centralized Parameter Label Configuration
 *
 * This file provides standardized labels, tooltips, and help text for all
 * inventory and replenishment parameters across the Warbler application.
 *
 * Usage:
 *   import { PARAMETER_LABELS, getParameterLabel } from '@/config/parameterLabels'
 *
 * Note: This only affects UI display. Data models and API contracts remain unchanged.
 */

export interface ParameterLabelConfig {
  label: string;
  short: string;
  tooltip: string;
  hint?: string;
}

/**
 * Standardized parameter labels for all inventory parameters
 */
export const PARAMETER_LABELS = {
  // Minimum Stock - Quantity Based
  MIN_STOCK_QUANTITY: {
    label: 'Minimum Stock Level (units)',
    short: 'Min Stock',
    tooltip: 'The minimum inventory level to maintain. When stock falls below this level, a reorder is triggered.',
    hint: 'Minimum inventory level in units. Triggers reorder when stock falls below this threshold.',
  },

  // Minimum Stock - Days Based
  MIN_STOCK_DAYS: {
    label: 'Minimum Stock Coverage (days)',
    short: 'Min Stock',
    tooltip: 'The minimum days of inventory to maintain based on forecasted demand.',
    hint: 'Minimum days of sales the inventory level should cover.',
  },

  // Maximum Stock - Quantity Based
  MAX_STOCK_QUANTITY: {
    label: 'Maximum Stock Level (units)',
    short: 'Max Stock',
    tooltip: 'The maximum inventory level to maintain. Orders will be adjusted to not exceed this level.',
    hint: 'Maximum inventory level in units.',
  },

  // Maximum Stock - Days Based
  MAX_STOCK_DAYS: {
    label: 'Maximum Stock Coverage (days)',
    short: 'Max Stock',
    tooltip: 'The maximum days of inventory to maintain. Orders will be adjusted to not exceed this level.',
    hint: 'Maximum days of sales the inventory level should cover.',
  },

  // Target Stock - Quantity Based
  TARGET_STOCK_QUANTITY: {
    label: 'Target Stock Level (units)',
    short: 'Target Stock',
    tooltip: 'The optimal inventory level to maintain for balanced stock availability and carrying costs.',
    hint: 'Optimal inventory level to maintain.',
  },

  // Target Stock - Days Based
  TARGET_STOCK_DAYS: {
    label: 'Target Stock Coverage (days)',
    short: 'Target Stock',
    tooltip: 'The optimal days of inventory coverage to maintain for balanced stock availability and carrying costs.',
    hint: 'Target days of inventory coverage based on forecasted demand.',
  },

  // Safety Stock - Quantity Based
  SAFETY_STOCK_QUANTITY: {
    label: 'Safety Stock (units)',
    short: 'Safety Stock',
    tooltip: 'Buffer inventory representing the minimum stock level to maintain in your store. Acts as protection against demand variability and supply delays.',
    hint: 'Minimum buffer inventory to maintain. Typically 10-20% of lead time demand.',
  },

  // Safety Stock - Days Based
  SAFETY_STOCK_DAYS: {
    label: 'Safety Stock Coverage (days)',
    short: 'Safety Stock',
    tooltip: 'Buffer inventory in days representing the minimum stock level to maintain in your store. Acts as protection against demand variability and supply delays. Recommended: 10-20% of your vendor lead time.',
    hint: 'Minimum buffer inventory in days. Recommended: 10-20% of vendor lead time.',
  },

  // Lead Time
  LEAD_TIME: {
    label: 'Lead Time (days)',
    short: 'Lead Time',
    tooltip: 'The time between placing an order and receiving the goods from this supplier.',
    hint: 'Time (in days) between placing an order with a supplier and receiving the items.',
  },

  // Order Cycle / Review Period
  ORDER_CYCLE: {
    label: 'Order Cycle (days)',
    short: 'Order Cycle',
    tooltip: 'The number of days of future sales that each order should cover. For example, if set to 90, your order quantity will be calculated to cover 90 days of expected sales.',
    hint: 'The number of days of future sales that each order should cover. E.g., set to 90 to order inventory covering 90 days of sales.',
  },

  // Reorder Point
  REORDER_POINT: {
    label: 'Reorder Point',
    short: 'Reorder Point',
    tooltip: 'The inventory level that triggers a reorder. Calculated as Safety Stock + (Forecast Daily Sales × Lead Time).',
    hint: 'Inventory level that triggers a reorder.',
  },

  // Fixed Order Quantity
  FIXED_ORDER_QUANTITY: {
    label: 'Fixed Order Quantity',
    short: 'Fixed Order Qty',
    tooltip: 'A specific quantity to order each time, regardless of current inventory levels.',
    hint: 'Fixed quantity to order each time.',
  },

  // Minimum Order Quantity
  MIN_ORDER_QUANTITY: {
    label: 'Minimum Order Quantity',
    short: 'Min Order Qty',
    tooltip: 'The smallest quantity that can be ordered from this supplier.',
    hint: 'Minimum quantity that can be ordered.',
  },

  // Maximum Order Quantity
  MAX_ORDER_QUANTITY: {
    label: 'Maximum Order Quantity',
    short: 'Max Order Qty',
    tooltip: 'The largest quantity that can be ordered at one time from this supplier.',
    hint: 'Maximum quantity that can be ordered at one time.',
  },

  // Case Quantity
  CASE_QUANTITY: {
    label: 'Case Quantity (units/case)',
    short: 'Case Qty',
    tooltip: 'Number of units per case. Orders may be rounded to whole cases.',
    hint: 'Number of units per case.',
  },
} as const;

/**
 * Type alias for parameter keys to provide compile-time validation and IDE autocomplete
 */
export type ParamKey = keyof typeof PARAMETER_LABELS;

/**
 * Helper function to get parameter label
 *
 * @param paramKey - The parameter key (e.g., 'MIN_STOCK_QUANTITY', 'LEAD_TIME')
 * @param format - The format ('label', 'short', 'tooltip', or 'hint')
 * @returns The appropriate label string
 */
export function getParameterLabel(
  paramKey: ParamKey,
  format: 'label' | 'short' | 'tooltip' | 'hint' = 'label'
): string {
  const config = PARAMETER_LABELS[paramKey];

  if (!config) {
    console.warn(`Parameter label config not found for key: ${paramKey}`);
    return paramKey;
  }

  if (format === 'hint') {
    return config.hint || '';
  }

  return config[format];
}

/**
 * Helper function to get all label information for a parameter
 *
 * @param paramKey - The parameter key
 * @returns Object containing label, short, tooltip, and hint
 */
export function getParameterLabelInfo(
  paramKey: ParamKey
): { label: string; short: string; tooltip: string; hint: string } {
  return {
    label: getParameterLabel(paramKey, 'label'),
    short: getParameterLabel(paramKey, 'short'),
    tooltip: getParameterLabel(paramKey, 'tooltip'),
    hint: getParameterLabel(paramKey, 'hint'),
  };
}

/**
 * Common parameter groups for displaying in forms
 */
export const PARAMETER_GROUPS = {
  INVENTORY_LEVELS_QUANTITY: ['MIN_STOCK_QUANTITY', 'MAX_STOCK_QUANTITY', 'TARGET_STOCK_QUANTITY', 'SAFETY_STOCK_QUANTITY'],
  INVENTORY_LEVELS_DAYS: ['MIN_STOCK_DAYS', 'MAX_STOCK_DAYS', 'TARGET_STOCK_DAYS', 'SAFETY_STOCK_DAYS'],
  TIME_PARAMETERS: ['LEAD_TIME', 'ORDER_CYCLE'],
  ORDER_QUANTITIES: ['FIXED_ORDER_QUANTITY', 'MIN_ORDER_QUANTITY', 'MAX_ORDER_QUANTITY', 'CASE_QUANTITY'],
  REPLENISHMENT: ['REORDER_POINT'],
};

/**
 * Default export for convenience
 */
export default {
  PARAMETER_LABELS,
  getParameterLabel,
  getParameterLabelInfo,
  PARAMETER_GROUPS,
};
